/**
 * Loved Treats tabs.
 *
 * Each tab swaps the product shown in the media gallery and buy box. The section is fetched at the
 * product's URL (`/products/handle?section_id=...`), so Liquid renders it for that product, then the
 * <featured-product-information> panel is swapped in. Variant changes inside the panel are handled by
 * variant-picker.js, which uses the same section_id fetch for <featured-product-information>.
 */
class LovedTreatsComponent extends HTMLElement {
  /** @type {Map<string, Promise<string>>} */
  #cache = new Map();

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePrefetch, true);
    this.addEventListener('focusin', this.#handlePrefetch);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePrefetch, true);
    this.removeEventListener('focusin', this.#handlePrefetch);
    this.#abortController?.abort();
  }

  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('[data-loved-treats-tab]')));
  }

  /**
   * @param {HTMLElement} tab
   * @returns {string}
   */
  #requestUrl(tab) {
    return `${tab.dataset.productUrl}?section_id=${this.dataset.sectionId}`;
  }

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  #fetchSection(url) {
    let request = this.#cache.get(url);

    if (!request) {
      request = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Failed to load ${url}`);
        return response.text();
      });
      request.catch(() => this.#cache.delete(url));
      this.#cache.set(url, request);
    }

    return request;
  }

  /** @param {Event} event */
  #handlePrefetch = (event) => {
    const tab = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-loved-treats-tab]') : null
    );
    if (!tab || tab.getAttribute('aria-pressed') === 'true') return;

    this.#fetchSection(this.#requestUrl(tab)).catch(() => {});
  };

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const tab = /** @type {HTMLButtonElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-loved-treats-tab]') : null
    );
    if (!tab || tab.getAttribute('aria-pressed') === 'true') return;

    this.#selectTab(tab);

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const panel = this.querySelector('featured-product-information');
    panel?.setAttribute('aria-busy', 'true');

    try {
      const html = await this.#fetchSection(this.#requestUrl(tab));
      if (abortController.signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newPanel = doc.querySelector('featured-product-information');
      const currentPanel = this.querySelector('featured-product-information');

      if (!newPanel || !currentPanel) throw new Error('Loved Treats: product panel not found');

      // Import (rather than morph) so the buy box components start fresh for the new product
      currentPanel.replaceWith(document.importNode(newPanel, true));
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error(error);
      // Fall back to the product page so the click still goes somewhere useful
      if (tab.dataset.productUrl) window.location.href = tab.dataset.productUrl;
    } finally {
      if (this.#abortController === abortController) {
        this.querySelector('featured-product-information')?.removeAttribute('aria-busy');
      }
    }
  };

  /** @param {HTMLButtonElement} selectedTab */
  #selectTab(selectedTab) {
    for (const tab of this.#tabs) {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-pressed', String(isSelected));
    }
  }
}

if (!customElements.get('loved-treats-component')) {
  customElements.define('loved-treats-component', LovedTreatsComponent);
}
